import { Chip } from '@mui/material';
import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { McpAccessProfile } from '../../../../Api/McpAccess';
import { formatLastUsed } from '../../../Compounds/McpAccess/formatLastUsed';
import { formatOptionalText } from '../../../Compounds/McpAccess/formatOptionalText';
import { McpAccessActionCell } from '../../../Compounds/McpAccess/McpAccessActionCell';
import { DetailCTARenderer } from '../../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';

const SCOPE_LABEL: Record<McpAccessProfile['scope'], string> = {
  read_only: 'Read only',
  read_write: 'Read & write'
};

export const DefaultMcpAccessGridColumnDefs: GridSingleSelectColDef[] = [
  {
    field: 'details',
    headerName: '',
    sortable: false,
    width: 65,
    filterable: false,
    hideable: false,
    disableReorder: true,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value=""
        valueFormatted=""
      />
    )
  },
  {
    field: 'title',
    headerName: 'Title',
    sortable: true,
    flex: 1,
    type: 'singleSelect',
    valueOptions: []
  },
  {
    field: 'access_id',
    headerName: 'Access ID',
    sortable: false,
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params: GridCellParams) => (
      <code style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{String(params.value ?? '')}</code>
    )
  },
  {
    field: 'scope',
    headerName: 'Scope',
    sortable: true,
    width: 140,
    type: 'singleSelect',
    valueOptions: [],
    renderCell: (params: GridCellParams) => {
      const scope = params.value as McpAccessProfile['scope'];
      return (
        <Chip
          size="small"
          label={SCOPE_LABEL[scope]}
          color={scope === 'read_write' ? 'primary' : 'default'}
        />
      );
    }
  },
  {
    field: 'last_used_at',
    headerName: 'Last used',
    sortable: true,
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value: string | null) => formatLastUsed(value)
  },
  {
    field: 'last_used_tool_name',
    headerName: 'Last tool',
    sortable: true,
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value: string | null) => formatOptionalText(value)
  },
  {
    field: 'last_used_client_name',
    headerName: 'Last client',
    sortable: true,
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value: string | null) => formatOptionalText(value)
  },
  {
    field: 'created_at',
    headerName: 'Created',
    sortable: true,
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    valueFormatter: (value: string | null) => formatLastUsed(value)
  }
];

export interface McpAccessActionsColumnConfig {
  submittingRotateId: string | null;
  submittingRevokeId: string | null;
  onRotate: (profile: McpAccessProfile) => void;
  onRevoke: (profile: McpAccessProfile) => void;
}

export const CreateMcpAccessActionsColumn = ({
  submittingRotateId,
  submittingRevokeId,
  onRotate,
  onRevoke
}: McpAccessActionsColumnConfig): GridSingleSelectColDef => ({
  field: 'actions',
  headerName: 'Actions',
  sortable: false,
  filterable: false,
  hideable: false,
  width: 120,
  type: 'singleSelect',
  valueOptions: [],
  renderCell: (params: GridCellParams) => {
    const profile = params.row as McpAccessProfile;
    return (
      <McpAccessActionCell
        profile={profile}
        rotating={submittingRotateId === profile.access_id}
        revoking={submittingRevokeId === profile.access_id}
        onRotate={onRotate}
        onRevoke={onRevoke}
      />
    );
  }
});
